import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { Temporal } from 'temporal-polyfill';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { EntityType } from '../../../../../data-models/field2.data-model';
import { Commentary, createEmptyCommentary, NewCommentaryId } from '../../../../../schemas/Commentary.schema';
import {
  createOrUpdateCommentary,
  deleteCommentaryById,
} from '../../../../../services/queries/MaggieCommentaryQueries';
import {
  commentariesByCompanyIdState,
  investmentThesisByCompanyIdAtom,
} from '../../../../../services/state/CommentariesState';
import { LoadingId } from '../../../../../types';
import { captureUnexpectedState } from '../../../../../util/SentryUtils';
import { trackEvent } from '../../../../../util/datadogTracking';
import { selectedCommentaryStateJ } from '../../../state/UIStateJ';

export function useGetTodaysCommentary(companyId: number) {
  const today = Temporal.Now.plainDateISO().toString();
  const commentaries = useAtomValue(commentariesByCompanyIdState(companyId));
  const todaysCommentary = commentaries.find((commentary) => {
    return commentary.date === today && commentary.entity === EntityType.company;
  });

  return (
    todaysCommentary ??
    createEmptyCommentary({
      entity: EntityType.company,
      entityId: companyId,
      date: today,
      id: NewCommentaryId,
    })
  );
}

export function useGetCompanyInvestmentThesis(companyId: number) {
  const commentaries = useAtomValue(commentariesByCompanyIdState(companyId));
  const theses = commentaries.filter((commentary) => {
    return commentary.entity === EntityType.companyInvestmentThesis;
  });

  if (theses.length > 1) {
    captureUnexpectedState('Multiple company investment theses found', {
      companyId,
      theses,
    });
  }

  return (
    theses[0] ??
    createEmptyCommentary({
      entity: EntityType.companyInvestmentThesis,
      entityId: companyId,
      date: '1900-01-01',
      id: NewCommentaryId,
    })
  );
}

export function useCreateOrUpdateCommentary() {
  const { pushErrorToast } = useToastMessageState();
  const setSelectedCommentary = useSetAtom(selectedCommentaryStateJ);

  const updateState = useAtomCallback(
    useCallback(
      (get, set, { companyId, updatedCommentary }: { companyId: number; updatedCommentary: Commentary }) => {
        if (updatedCommentary.entity === EntityType.companyInvestmentThesis) {
          set(investmentThesisByCompanyIdAtom(companyId), Promise.resolve([updatedCommentary]));
        }
      },
      []
    )
  );

  const createOrUpdate = useAtomCallback(
    // TODO: this doesn't match the behavior of createOrUpdateCommentary - the BE service is not checking the id for uniqueness,
    // but entityId, entity and date - if those are the same, it will update existing commentary, otherwise it will create a new one;
    // without the autosave feature, we can keep existing logic, but if we want to use autosave,
    // we need to change this - or change the BE service
    // (cf https://github.com/ForesightData/api/blob/15c8e22b4fbf8d32cba6af8a929d5fc6f6e6e8c6/src/commentary/commentary.service.ts#L9)
    useCallback(
      async (get, set, companyId: number, updatedCommentary: Commentary) => {
        try {
          const response = await createOrUpdateCommentary(updatedCommentary);
          const current = await get(commentariesByCompanyIdState(companyId));

          if (updatedCommentary.id === NewCommentaryId) {
            set(commentariesByCompanyIdState(companyId), [response, ...current]);
            trackEvent('company.commentary_added', { entity: updatedCommentary.entity });
          } else {
            set(
              commentariesByCompanyIdState(companyId),
              current.map((c) => (c.id === response.id ? response : c))
            );
          }
          setSelectedCommentary(response);
          updateState({ companyId, updatedCommentary: response });
          return response;
        } catch (err) {
          console.error(err);
          pushErrorToast({ message: 'Failed to update commentary' });
          return null;
        }
      },
      [pushErrorToast, setSelectedCommentary, updateState]
    )
  );

  return createOrUpdate;
}

export function useDeleteCommentary() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, companyId: number, noteId: number) => {
        actions.startLoading(LoadingId.deleteNote);
        const currentCommentaries = await get(commentariesByCompanyIdState(companyId));
        const deletedCommentary = currentCommentaries.find((c) => c.id === noteId);
        const updatedCommentaries = currentCommentaries.filter((c) => c.id !== noteId);

        try {
          await deleteCommentaryById(noteId);
          set(commentariesByCompanyIdState(companyId), updatedCommentaries);

          if (deletedCommentary?.entity === EntityType.companyInvestmentThesis) {
            const currentTheses = await get(investmentThesisByCompanyIdAtom(companyId));
            set(
              investmentThesisByCompanyIdAtom(companyId),
              Promise.resolve(currentTheses.filter((t) => t.id !== noteId))
            );
          }

          pushSuccessToast({ message: 'Performance commentary deleted' });
          return true;
        } catch (err) {
          set(commentariesByCompanyIdState(companyId), currentCommentaries);
          pushErrorToast({ message: 'Failed to delete performance commentary' });
          return false;
        } finally {
          actions.stopLoading(LoadingId.deleteNote);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );
}
